import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderMarkdown(text) {
  if (!text) return "";
  let html = text
    .replace(/### (.*?)(\n|$)/g, '<h3>$1</h3>')
    .replace(/## (.*?)(\n|$)/g, '<h2>$1</h2>')
    .replace(/# (.*?)(\n|$)/g, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*?)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');
  return `<p>${html}</p>`;
}

export default function BlogPostPage() {
  const { blogId } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blogs/${blogId}`)
      .then((res) => setBlog(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [blogId]);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-8 w-8 animate-spin text-[#002147]" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] px-4">
        <h2 className="font-heading text-2xl font-bold text-[#002147] mb-4">Blog Post Not Found</h2>
        <Link to="/blog">
          <Button className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      {/* Header */}
      <section className="hero-gradient py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-[#D4AF37] hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Blog
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-0">{blog.category}</Badge>
            {blog.tags?.map((tag, i) => (
              <Badge key={i} variant="outline" className="border-white/20 text-white/60 text-xs">{tag}</Badge>
            ))}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            {blog.title}
          </h1>
          <div className="flex items-center gap-4 text-white/60 text-sm">
            <span className="flex items-center gap-1"><User className="h-4 w-4" />{blog.author}</span>
            <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{formatDate(blog.created_at)}</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid="blog-content"
            className="blog-content prose prose-slate max-w-none"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(blog.content) }}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F8FAFC] py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="font-heading text-2xl font-bold text-[#002147] mb-3">Need Insurance Advice?</h3>
          <p className="text-slate-500 mb-6">Our team is here to help you find the right coverage. Contact us today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button data-testid="blog-cta-quote" className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold">
                Get a Free Quote
              </Button>
            </Link>
            <Link to="/blog">
              <Button variant="outline" className="border-[#002147] text-[#002147] hover:bg-[#002147] hover:text-white">
                More Articles
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
